import * as React from "react";
import { useEffect, useState } from "react";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import useApi from "@/hooks/useApi";
import { WorkflowNode } from "../../types";

interface WorkflowListItem {
  id: number;
  name: string;
  isTemplate?: boolean;
}

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function CallWorkflowEditor({ node, onUpdate }: Props) {
  const api = useApi();
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const resp = await api.messaging.get("Workflows/List");
        const data = await resp.json();
        setWorkflows(Array.isArray(data) ? data : []);
      } catch {
        setWorkflows([]);
      }
    }
    load();
  }, []);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? workflows.filter((w) => (w.name || "").toLowerCase().includes(q))
    : workflows;

  const options: Record<number, string> = {};
  for (const w of filtered) {
    options[w.id] = w.isTemplate ? `📋 Reusable  ${w.name}` : w.name;
  }

  return (
    <>
      <FormGroup
        label="Workflow to call"
        tooltipText="Select a workflow to run as a sub-workflow at this point. Its nodes will execute inline, then this workflow continues to the next step."
      >
        <input
          type="text"
          placeholder="Search workflows..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full mb-2 px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        {filtered.length === 0 ? (
          <div className="px-1 py-2 text-xs text-slate-500">
            No workflows match your search.
          </div>
        ) : (
          <Select
            options={options}
            value={node.referencedWorkflowId}
            onChange={(value) => onUpdate({ referencedWorkflowId: value })}
          />
        )}
      </FormGroup>
      <div className="px-1 py-2 text-xs text-slate-400 leading-snug">
        The called workflow runs within this job&apos;s step budget. Interactive nodes
        (Ask Question, Time Delay, Request Payment) inside a called workflow are skipped.
        Each invocation counts as one additional workflow run.
      </div>
    </>
  );
}
